export type StoryAnswer = {
  entity: string;
  question: string;
  parts: { text: string; source?: number }[];
  records: { name: string; title: string; system: string; value: string; detail: string }[];
};

/** An answer's sentence, whole. */
export function answerText(answer: StoryAnswer): string {
  return answer.parts.map((part) => part.text).join("");
}

/**
 * The systems the answers' records name, as the product connects them: the
 * plugin each comes through and its mark (public/concepts/source-icons).
 */
const SYSTEMS: Record<string, { slug: string; label: string; icon: string }> = {
  Xero: { slug: "xero", label: "Xero", icon: "xero.svg" },
  Linear: { slug: "linear", label: "Linear", icon: "linear.png" },
  Gmail: { slug: "gmail", label: "Gmail", icon: "gmail.png" },
  SharePoint: { slug: "sharepoint", label: "SharePoint", icon: "sharepoint.svg" },
  Sheets: { slug: "google-sheets", label: "Google Sheets", icon: "google-sheets.png" },
  "Google Sheets": { slug: "google-sheets", label: "Google Sheets", icon: "google-sheets.png" },
  Slack: { slug: "slack", label: "Slack", icon: "slack.png" },
  "Microsoft Teams": { slug: "microsoft-teams", label: "Microsoft Teams", icon: "teams.svg" },
  "Google Docs": { slug: "google-docs", label: "Google Docs", icon: "google-docs.png" },
  Overture: { slug: "overture", label: "Overture", icon: "overture.svg" },
  SunSystems: { slug: "sun", label: "SunSystems", icon: "sunsystems.svg" },
};

/** The systems an answer was retrieved with, in its records' order, each once. */
export function answerSources(answer: StoryAnswer): { slug: string; label: string; iconUrl: string }[] {
  const sources = new Map<string, { slug: string; label: string; iconUrl: string }>();
  for (const record of answer.records) {
    const name = record.system.split(" / ")[0]!;
    const system = SYSTEMS[name];
    if (!system) throw new Error(`The story's answers name a system with no mark: "${name}".`);
    sources.set(system.slug, { slug: system.slug, label: system.label, iconUrl: `/concepts/source-icons/${system.icon}` });
  }
  return [...sources.values()];
}

export const NORTHSTAR_ANSWER: StoryAnswer = {
  entity: "Northstar",
  question: "Where are we with Northstar?",
  parts: [{ text: "The " }, { text: "website is delivered", source: 1 }, { text: ". The " }, { text: "£8,400 invoice is overdue", source: 0 }, { text: ", and the client " }, { text: "expects to pay Friday", source: 2 }, { text: "." }],
  records: [
    { name: "Invoice", title: "INV-2048", system: "Xero / Invoice record", value: "£8,400 outstanding", detail: "Customer: Northstar. The invoice is overdue and has not yet been paid." },
    { name: "Project", title: "Northstar website", system: "Linear / Project update", value: "Delivery complete", detail: "The website has been delivered. There is no remaining delivery blocker in this example." },
    { name: "Conversation", title: "Latest client update", system: "Gmail / Client email", value: "Payment expected Friday", detail: "“We’ve scheduled the payment for Friday. Thanks for getting the website over the line.”" },
  ],
};

export const ACCOUNTANT_ANSWER: StoryAnswer = {
  entity: "Alder & Co", question: "Is Alder ready for Thursday’s review?",
  parts: [{ text: "The " }, { text: "supporting schedule is missing", source: 0 }, { text: ". " }, { text: "Priya owns the follow-up", source: 2 }, { text: " ahead of " }, { text: "Thursday’s partner review", source: 1 }, { text: "." }],
  records: [
    { name: "Checklist", title: "AC-026 · Supporting schedule", system: "SharePoint / Checklist", value: "11 of 12 items received", detail: "The supporting schedule is outstanding. The accounts still need the partner’s review and approval." },
    { name: "Review", title: "Partner review", system: "Sheets / Engagement tracker", value: "Thursday", detail: "Alder & Co’s review is scheduled for Thursday. The supporting schedule is still needed for the review." },
    { name: "Follow-up", title: "Client request recorded", system: "Slack / Review follow-up", value: "Owner · Priya Shah", detail: "Priya’s Slack update records the request for the remaining schedule. The schedule is not yet marked as received." },
  ],
};

export const CONSULTANCY_ANSWER: StoryAnswer = {
  entity: "Meridian", question: "Can Meridian launch on Friday?",
  parts: [{ text: "The launch is " }, { text: "planned for Friday", source: 0 }, { text: ", but " }, { text: "client sign-off is still pending", source: 1 }, { text: ". " }, { text: "Alex owns the follow-up", source: 2 }, { text: "." }],
  records: [
    { name: "Plan", title: "ML-028 · Launch", system: "Google Sheets / Launch plan", value: "Target: Friday", detail: "Meridian launch plan ML-028 lists Friday as the target. Final client sign-off is required before launch." },
    { name: "Decision", title: "Client checks still under way", system: "Microsoft Teams / Client update", value: "Waiting for sign-off", detail: "Wednesday client update: ‘We are still completing final checks. Please wait for our sign-off before confirming Friday.’" },
    { name: "Owner", title: "Confirm client approval", system: "Google Docs / Launch actions", value: "Owner · Alex Morgan", detail: "Launch actions ML-028: Alex to ask for sign-off on Thursday and update the plan after the client replies." },
  ],
};
